import { Injectable } from '@angular/core';

import { BsCacheService } from '@brightside-web/desktop/data-access/core-services';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { Environment } from '@micro-core/environment';

import { AmplitudeService } from './amplitude.service';
import { FirebaseService, RemoteConfig_DesktopNativeMapping } from './firebase-abstract.service';
import * as Sentry from '@sentry/angular';

import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  RemoteConfig,
  getRemoteConfig,
  fetchAndActivate,
  getValue,
  ensureInitialized,
  fetchConfig,
} from 'firebase/remote-config';
import { Analytics as FbAnalytics, getAnalytics, setUserProperties, setUserId, logEvent } from 'firebase/analytics';
import { BehaviorSubject, firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { CopyOverridesInterface } from '@brightside-web/desktop/data-access/shared';
import { filter, first, map } from 'rxjs/operators';

const defaultMapping = {
  'partner/Credit_Card_Refi': {
    path: 'loans',
  },
  'partner/Emergency_Cash': {
    path: 'loans',
  },
  'finsol/deep/savings/autosave/once': {
    path: 'home/deep/savings/intro',
    queryParamsType: 'auto',
  },
  home: {
    path: 'home',
  },
  onboarding: {
    path: 'onboarding',
  },
  setupcredit: {
    path: 'credit-setup/creation',
  },
};

export function parseRemoteConfigArray<T>(value: string | null | undefined): T[] {
  if (!value) return [];

  try {
    const firstParse = JSON.parse(value);

    // Case 1: Already a valid array
    if (Array.isArray(firstParse)) return firstParse;

    // Case 2: It's a string that contains JSON (double-encoded)
    if (typeof firstParse === 'string') {
      const secondParse = JSON.parse(firstParse);
      if (Array.isArray(secondParse)) return secondParse;
    }

    return [];
  } catch (e) {
    return [];
  }
}

const pageShownAppend = ['video tapped', 'logo tapped'];

interface ThrottledEvent {
  lastCall: number;
  throttleTime: number;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseAnalyticsService implements FirebaseService {
  private _remoteConfigReady = new BehaviorSubject<boolean>(false);
  remoteConfigReady$ = this._remoteConfigReady.asObservable();
  get remoteConfigReadyPromise(): Promise<void> {
    return firstValueFrom(
      this.remoteConfigReady$.pipe(
        // Wait until it's true
        filter(ready => ready === true),
        first(),
        map(() => undefined)
      )
    );
  };

  sub = new Subscription();

  firebaseApp: FirebaseApp;
  fbRemoteConfig: RemoteConfig;
  fbAnalytics: FbAnalytics;

  events: { [p: string]: ThrottledEvent } = {};
  lastPageShownEvent: string;

  onboardingFlowActive: boolean;

  typeOveride: { [p: string]: () => Promise<string> } = {
    credit: async () => ((await this.bsCacheService.getItem('ONBOARDING_FLOW_ACTIVE')) ? 'onboarding' : 'standalone'),
    'onboarding credit': async () =>
      (await this.bsCacheService.getItem('ONBOARDING_FLOW_ACTIVE')) ? 'onboarding' : 'standalone',
  };

  typeOverRideKeys: string[];
  _guid: string;

  constructor(public amplitudeService: AmplitudeService, public env: Environment, protected bsCacheService: BsCacheService) {
    this.typeOverRideKeys = Object.keys(this.typeOveride);
    this.firebaseApp = this.initializeApp();
    this.fbAnalytics = this.getAnalytics();
  }

  initializeApp() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return initializeApp(this.env.firebase);
  }

  getRemoteConfig() {
    return getRemoteConfig(this.firebaseApp);
  }

  getAnalytics() {
    return getAnalytics(this.firebaseApp);
  }

  async logEvent(
    eventName: string,
    eventParams?: { custom: { [p: string]: any } | string; [p: string]: any },
    shouldThrottle: boolean = false,
    throttleTime?: number
  ): Promise<void> {
    if (eventName && eventName !== 'onboardingcredit shown') {
      if (eventName.includes('shown')) this.lastPageShownEvent = eventName.split('shown')[0].trim();
      if (pageShownAppend.includes(eventName)) eventName = `${this.lastPageShownEvent} ${eventName}`;

      const isEventThrottled = shouldThrottle === false ? false : this.isEventThrottled(eventName, throttleTime);

      if (!isEventThrottled) {
        let params = eventParams ?? {};

        if (eventParams?.custom && typeof eventParams?.custom === 'object') {
          eventParams.custom = JSON.stringify(eventParams?.custom);
          params = eventParams.custom;
        }
        // todo: KEN DOUBLE CHECK THIS
        for (let i = 0; i < this.typeOverRideKeys.length; i++) {
          if (eventName.startsWith(this.typeOverRideKeys[i])) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            params['type'] = await this.typeOveride[this.typeOverRideKeys[i]]();
            break;
          }
        }
        if (eventName.split(' ').join('_') !== 'api_call_completed' &&
            eventName.split(' ').join('_') !== 'api_call_started') {
          this.amplitudeService.logEvent(eventName.split('_').join(' '), params);
        }
        Sentry.logger.info(eventName.split(' ').join('_'), {...params, guid: this._guid ?? ''});
        logEvent(this.fbAnalytics, eventName, eventParams);
        if (!this.env.production) {
          console.log(`Analytics event fired: ${eventName.split('_').join(' ')} with `, params);
        }
      }
    }
  }

  isEventThrottled(eventName: string, throttleTime?: number): boolean {
    const event = this.events[eventName];
    const currentCall = Date.now();

    if (event) {
      if (currentCall - event.lastCall > event.throttleTime) {
        event.lastCall = currentCall;
        return false;
      } else {
        return true;
      }
    } else {
      this.events[eventName] = {
        lastCall: currentCall,
        throttleTime: throttleTime ? throttleTime : 1000,
      };
      return false;
    }
  }

  setUserId(id: string, options?: any): void {
    this._guid = id;
    setUserId(this.fbAnalytics, id, options);
    this.amplitudeService.setUserId(id);
  }

  clearProperties(): void {
    setUserId(this.fbAnalytics, '');
    setUserProperties(this.fbAnalytics, {});
  }

  setProperties(properties: { [key: string]: string }) {
    setUserProperties(this.fbAnalytics, properties);
  }

  setupRemoteConfig() {
    fetchAndActivate(this.fbRemoteConfig)
      .then(() => {})
      .catch((error) => {
        Sentry.captureException(error);
      });
  }

  remoteConfig(): RemoteConfig {
    return this.fbRemoteConfig;
  }

  getValueAsString(valueKey: string): string {
    return getValue(this.fbRemoteConfig, valueKey).asString();
  }

  getValueAsBoolean(valueKey: string): boolean {
    return getValue(this.fbRemoteConfig, valueKey).asBoolean();
  }

  getValueAsArray<T>(valueKey: string): T[] {
    return parseRemoteConfigArray<T>(this.getValueAsString(valueKey));
  }

  ensureInitialized(): Promise<void> {
    return ensureInitialized(this.fbRemoteConfig);
  }

  fetchConfig(): Promise<void> {
    return fetchConfig(this.fbRemoteConfig);
  }

  remoteConfigMapping(): { [key: string]: any } {
    try {
      return JSON.parse(getValue(this.fbRemoteConfig, RemoteConfig_DesktopNativeMapping).asString()) as { [key: string]: any };
    } catch (e) {
      return defaultMapping;
    }
    return defaultMapping;
  }

  copyOverridesMapping() : CopyOverridesInterface[] | [] {
    try {
      return JSON.parse(getValue(this.fbRemoteConfig, 'copyOverride').asString()) as CopyOverridesInterface[] ;
    } catch (e) {
      return [];
    }
  }
}
